/**
 * Payroll Review Page (Phase 1, read-only).
 *
 * Two tabs:
 *   - Entries: filtered employee × month rows
 *   - Files:   FileIngestionLog health view per company
 *
 * Confidential by design — guarded by /payroll-review page permission.
 */

import { useEffect, useMemo, useState } from 'react';
import { Loader2, AlertCircle, FileText, Users, ShieldAlert, ExternalLink, Search, Filter } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import {
    listPayrollEntries,
    listPayrollFiles,
    type PayrollEntry,
    type PayrollStatRow,
    type PayrollFile,
    type PayrollFilesSummaryRow,
} from '../services/payrollApi';

const COUNTRY_OPTIONS = [
    { code: '', label: 'All countries' },
    { code: 'dch', label: '🇨🇭 Switzerland (Dolder)' },
    { code: 'dbi', label: '🇨🇭 Switzerland (Bigler)' },
    { code: 'dde', label: '🇩🇪 Germany' },
    { code: 'dat', label: '🇦🇹 Austria' },
    { code: 'dfr', label: '🇫🇷 France' },
    { code: 'dsp', label: '🇪🇸 Spain' },
    { code: 'dpt', label: '🇵🇹 Portugal' },
    { code: 'dtr', label: '🇹🇷 Türkiye' },
    { code: 'dit', label: '🇮🇹 Italy (locked)' },
];

const MONTH_OPTIONS = [
    { v: 0, label: 'All months' },
    { v: 1, label: 'Jan' }, { v: 2, label: 'Feb' }, { v: 3, label: 'Mar' }, { v: 4, label: 'Apr' },
    { v: 5, label: 'May' }, { v: 6, label: 'Jun' }, { v: 7, label: 'Jul' }, { v: 8, label: 'Aug' },
    { v: 9, label: 'Sep' }, { v: 10, label: 'Oct' }, { v: 11, label: 'Nov' }, { v: 12, label: 'Dec' },
];

function fmtNum(n: number | null | undefined, currency: string): string {
    if (n === null || n === undefined) return '—';
    return new Intl.NumberFormat('de-CH', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(n);
}

function fmtYearMonth(ym: number): string {
    const y = Math.floor(ym / 100);
    const m = ym % 100;
    return `${String(m).padStart(2, '0')}/${y}`;
}

function parseFlags(raw: string | null): string[] {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter(s => typeof s === 'string') : [];
    } catch {
        return [];
    }
}

function flagSeverity(flag: string): 'warn' | 'info' {
    if (flag.startsWith('no_employee_match')) return 'warn';
    if (flag.startsWith('socialEmployer_could_not_be_calculated')) return 'warn';
    if (flag.startsWith('yearmonth_mismatch')) return 'warn';
    if (flag.startsWith('manual_check_needed')) return 'warn';
    // Gross split (base / bonus / car private share) is not trustworthy on this row.
    if (flag.startsWith('bonus_not_separable')) return 'warn';
    if (flag.startsWith('car_share_not_separable')) return 'warn';
    if (flag.startsWith('gross_split_implausible')) return 'warn';
    if (flag.startsWith('gross_recomputed_server_side')) return 'warn';
    if (flag.startsWith('bvg_possible_double_count')) return 'warn';
    return 'info';
}

function emailMatchBadge(email: string, flags: string[]): JSX.Element {
    if (!email) {
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700" title="No employee match in etl.EmployeeMaster">no match</span>;
    }
    if (flags.includes('match_subset')) {
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700" title="Matched via subset (one surname out of several)">subset</span>;
    }
    if (flags.includes('match_tokenset')) {
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700" title="Matched via token-set (order-insensitive)">tokens</span>;
    }
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700" title="Exact match">exact</span>;
}

function inactiveBadge(): JSX.Element {
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700" title="Employee has left the company (LeaveDate <= today)">former</span>;
}

function StatusPill({ status }: { status: string }) {
    const map: Record<string, string> = {
        Success: 'bg-green-100 text-green-700',
        Failed: 'bg-red-100 text-red-700',
        Unparseable: 'bg-orange-100 text-orange-700',
        Skipped: 'bg-gray-100 text-gray-600',
        Filtered: 'bg-gray-50 text-gray-500',
    };
    const cls = map[status] ?? 'bg-gray-100 text-gray-600';
    return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{status}</span>;
}

export default function PayrollReviewPage() {
    const { canView, canEdit, isLoading: permLoading } = usePagePermissions('/payroll-review');

    const [tab, setTab] = useState<'entries' | 'files'>('entries');

    // Filters
    const [country, setCountry] = useState<string>('');
    const [year, setYear] = useState<number>(new Date().getFullYear());
    const [month, setMonth] = useState<number>(0); // 0 = all
    const [search, setSearch] = useState<string>('');
    const [debouncedSearch, setDebouncedSearch] = useState<string>('');

    // Data
    const [entries, setEntries] = useState<PayrollEntry[]>([]);
    const [stats, setStats] = useState<PayrollStatRow[]>([]);
    const [files, setFiles] = useState<PayrollFile[]>([]);
    const [filesSummary, setFilesSummary] = useState<PayrollFilesSummaryRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Debounce search
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(t);
    }, [search]);

    // Fetch entries on filter change
    useEffect(() => {
        if (!canView || tab !== 'entries') return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        listPayrollEntries({
            company: country || null,
            year: month > 0 ? year : null,
            month: month > 0 ? month : null,
            search: debouncedSearch || null,
        })
            .then(res => {
                if (cancelled) return;
                setEntries(res.entries);
                setStats(res.stats);
            })
            .catch(err => {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : String(err));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [canView, tab, country, year, month, debouncedSearch]);

    // Fetch files when files tab opens or country changes
    useEffect(() => {
        if (!canView || tab !== 'files') return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        listPayrollFiles({ company: country || null })
            .then(res => {
                if (cancelled) return;
                setFiles(res.files);
                setFilesSummary(res.summary);
            })
            .catch(err => {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : String(err));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [canView, tab, country]);

    const totalsByCurrency = useMemo(() => {
        const grouped = new Map<string, { gross: number; bonus: number; car: number; reported: number; social: number; net: number; rows: number }>();
        for (const s of stats) {
            const cur = grouped.get(s.Currency) ?? { gross: 0, bonus: 0, car: 0, reported: 0, social: 0, net: 0, rows: 0 };
            cur.gross += s.TotalGross || 0;
            cur.bonus += s.TotalBonus || 0;
            cur.car += s.TotalCarPrivateShare || 0;
            cur.reported += s.TotalGrossReported || 0;
            cur.social += s.TotalSocialEmployer || 0;
            cur.net += s.TotalNet || 0;
            cur.rows += s.RecordCount || 0;
            grouped.set(s.Currency, cur);
        }
        return Array.from(grouped.entries()).map(([currency, sums]) => ({ currency, ...sums }));
    }, [stats]);

    if (permLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    if (!canView) {
        return (
            <div className="p-8 max-w-2xl">
                <div className="flex items-start gap-3 p-4 border border-orange-200 bg-orange-50 rounded">
                    <ShieldAlert className="w-6 h-6 text-orange-600 mt-0.5" />
                    <div>
                        <h2 className="font-semibold text-orange-900">No access to Payroll Review</h2>
                        <p className="text-sm text-orange-800">This page contains highly confidential salary data. Access is granted on a per-user basis.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-4 max-w-[1600px]">
            <div className="flex items-center gap-3">
                <ShieldAlert className="w-7 h-7 text-orange-600" />
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Payroll Review</h1>
                    <p className="text-sm text-gray-500">
                        Phase 1: extracted monthly salary data per employee × company. Gross is the base
                        salary — bonus and car private share are shown separately, not inside it.
                        Confidential — every read is logged.
                    </p>
                </div>
            </div>

            {/* Filter bar */}
            <div className="flex flex-wrap items-end gap-3 p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="flex flex-col">
                    <label className="text-xs font-medium text-gray-600 mb-1 flex items-center gap-1"><Filter className="w-3 h-3" /> Country</label>
                    <select
                        className="border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
                        value={country}
                        onChange={e => setCountry(e.target.value)}
                    >
                        {COUNTRY_OPTIONS.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                    </select>
                </div>
                <div className="flex flex-col">
                    <label className="text-xs font-medium text-gray-600 mb-1">Year</label>
                    <input
                        type="number"
                        min={2020}
                        max={2099}
                        className="border border-gray-300 rounded px-2 py-1.5 text-sm w-24"
                        value={year}
                        onChange={e => setYear(parseInt(e.target.value, 10) || new Date().getFullYear())}
                    />
                </div>
                <div className="flex flex-col">
                    <label className="text-xs font-medium text-gray-600 mb-1">Month</label>
                    <select
                        className="border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
                        value={month}
                        onChange={e => setMonth(parseInt(e.target.value, 10))}
                    >
                        {MONTH_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                    </select>
                </div>
                <div className="flex flex-col flex-1 min-w-[200px]">
                    <label className="text-xs font-medium text-gray-600 mb-1 flex items-center gap-1"><Search className="w-3 h-3" /> Search employee</label>
                    <input
                        type="text"
                        placeholder="Name or email"
                        className="border border-gray-300 rounded px-2 py-1.5 text-sm"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-gray-200">
                <button
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'entries' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
                    onClick={() => setTab('entries')}
                >
                    <Users className="w-4 h-4 inline mr-1" /> Entries
                </button>
                <button
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'files' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
                    onClick={() => setTab('files')}
                >
                    <FileText className="w-4 h-4 inline mr-1" /> Files
                </button>
            </div>

            {error && (
                <div className="flex items-start gap-2 p-3 border border-red-200 bg-red-50 rounded">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                    <div className="text-sm text-red-800">{error}</div>
                </div>
            )}

            {loading && (
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
            )}

            {/* Entries tab */}
            {tab === 'entries' && !loading && (
                <>
                    {/* KPI tiles per currency */}
                    {totalsByCurrency.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            {totalsByCurrency.map(t => (
                                <div key={t.currency} className="p-3 bg-white border border-gray-200 rounded-lg">
                                    <div className="text-xs text-gray-500 mb-1">{t.currency} · {t.rows} rows</div>
                                    <div className="text-sm space-y-0.5">
                                        <div className="flex justify-between" title="Base salary — bonus and car private share excluded">
                                            <span className="text-gray-600">Gross (base)</span><span className="font-mono font-semibold">{fmtNum(t.gross, t.currency)}</span>
                                        </div>
                                        <div className="flex justify-between"><span className="text-gray-600">+ Bonus</span><span className="font-mono text-gray-500">{fmtNum(t.bonus, t.currency)}</span></div>
                                        <div className="flex justify-between" title="Private use of company car — added to gross on the payslip, deducted again from net">
                                            <span className="text-gray-600">+ Car private</span><span className="font-mono text-gray-500">{fmtNum(t.car, t.currency)}</span>
                                        </div>
                                        <div className="flex justify-between border-t border-gray-200 pt-0.5" title="Total gross as printed on the payslip">
                                            <span className="text-gray-600">= Total gross</span><span className="font-mono">{fmtNum(t.reported, t.currency)}</span>
                                        </div>
                                        <div className="flex justify-between"><span className="text-gray-600">Social-Empl.</span><span className="font-mono">{fmtNum(t.social || null, t.currency)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-600">Net</span><span className="font-mono">{fmtNum(t.net || null, t.currency)}</span></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Entries table */}
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                                    <tr>
                                        <th className="px-3 py-2 text-left font-medium text-gray-700">Period</th>
                                        <th className="px-3 py-2 text-left font-medium text-gray-700">Co.</th>
                                        <th className="px-3 py-2 text-left font-medium text-gray-700">Employee</th>
                                        <th className="px-3 py-2 text-left font-medium text-gray-700">P-No.</th>
                                        <th className="px-3 py-2 text-right font-medium text-gray-700" title="Base salary — bonus and car private share excluded">Gross</th>
                                        <th className="px-3 py-2 text-right font-medium text-gray-700">Bonus</th>
                                        <th className="px-3 py-2 text-right font-medium text-gray-700" title="Private use of company car — excluded from Gross">Car</th>
                                        <th className="px-3 py-2 text-right font-medium text-gray-700" title="Total gross as printed on the payslip">Total gross</th>
                                        <th className="px-3 py-2 text-right font-medium text-gray-700">Social-AG</th>
                                        <th className="px-3 py-2 text-right font-medium text-gray-700">Net</th>
                                        <th className="px-3 py-2 text-center font-medium text-gray-700">Conf.</th>
                                        <th className="px-3 py-2 text-left font-medium text-gray-700">Flags</th>
                                        <th className="px-3 py-2 text-left font-medium text-gray-700">Source</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {entries.length === 0 && (
                                        <tr><td colSpan={13} className="px-3 py-6 text-center text-gray-500">No entries match these filters.</td></tr>
                                    )}
                                    {entries.map(e => {
                                        const flags = parseFlags(e.Flags);
                                        const emailFlags = flags.filter(f => f.startsWith('match_') || f === 'no_employee_match');
                                        const otherFlags = flags.filter(f => !f.startsWith('match_') && f !== 'no_employee_match');
                                        return (
                                            <tr key={e.Id} className="hover:bg-blue-50/30">
                                                <td className="px-3 py-1.5 font-mono text-xs">{fmtYearMonth(e.YearMonth)}</td>
                                                <td className="px-3 py-1.5 font-mono text-xs uppercase">{e.CompanyCode}</td>
                                                <td className="px-3 py-1.5">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="font-medium">
                                                            {e.EmployeeFullName || e.EmployeeNameRaw}
                                                        </span>
                                                        {emailMatchBadge(e.EmployeeEmail, flags)}
                                                        {flags.includes('match_inactive') && inactiveBadge()}
                                                    </div>
                                                    {e.EmployeeEmail && <div className="text-xs text-gray-500">{e.EmployeeEmail}</div>}
                                                </td>
                                                <td className="px-3 py-1.5 font-mono text-xs text-gray-500">{e.PersonalNumber || '—'}</td>
                                                <td className="px-3 py-1.5 text-right font-mono">{fmtNum(e.GrossSalary, e.Currency)}</td>
                                                <td className="px-3 py-1.5 text-right font-mono text-gray-500">
                                                    {e.Bonus && e.Bonus > 0 ? fmtNum(e.Bonus, e.Currency) : '—'}
                                                </td>
                                                <td className="px-3 py-1.5 text-right font-mono text-gray-500">
                                                    {e.CarPrivateShare && e.CarPrivateShare > 0 ? fmtNum(e.CarPrivateShare, e.Currency) : '—'}
                                                </td>
                                                <td className="px-3 py-1.5 text-right font-mono text-gray-500">{fmtNum(e.GrossReported, e.Currency)}</td>
                                                <td className="px-3 py-1.5 text-right font-mono">{fmtNum(e.SocialEmployer, e.Currency)}</td>
                                                <td className="px-3 py-1.5 text-right font-mono">{fmtNum(e.NetSalary, e.Currency)}</td>
                                                <td className="px-3 py-1.5 text-center">
                                                    {e.ConfidenceScore !== null ? (
                                                        <span className={`text-xs font-medium ${e.ConfidenceScore >= 90 ? 'text-green-600' : e.ConfidenceScore >= 75 ? 'text-amber-600' : 'text-red-600'}`}>
                                                            {Number(e.ConfidenceScore).toFixed(0)}
                                                        </span>
                                                    ) : <span className="text-gray-400">—</span>}
                                                </td>
                                                <td className="px-3 py-1.5">
                                                    {emailFlags.length === 0 && otherFlags.length === 0 && <span className="text-gray-400">—</span>}
                                                    <div className="flex flex-wrap gap-1">
                                                        {otherFlags.map(f => (
                                                            <span
                                                                key={f}
                                                                className={`text-xs px-1.5 py-0.5 rounded ${flagSeverity(f) === 'warn' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}
                                                                title={f}
                                                            >
                                                                {f.length > 32 ? f.slice(0, 32) + '…' : f}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-1.5">
                                                    {e.SourceWebUrl ? (
                                                        <a href={e.SourceWebUrl} target="_blank" rel="noreferrer"
                                                           className="text-blue-600 hover:underline inline-flex items-center gap-1 text-xs"
                                                           title={e.SourceFileName ?? ''}>
                                                            <ExternalLink className="w-3 h-3" /> open
                                                        </a>
                                                    ) : <span className="text-gray-400">—</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="text-xs text-gray-500">
                        Showing {entries.length} entries.
                        {canEdit && (
                            <span className="ml-2 text-gray-400">
                                (Re-extraction trigger via API — see /api/v1/payroll/ingest)
                            </span>
                        )}
                    </div>
                </>
            )}

            {/* Files tab */}
            {tab === 'files' && !loading && (
                <>
                    {filesSummary.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {filesSummary.map(s => (
                                <div key={s.CompanyCode} className="p-3 bg-white border border-gray-200 rounded-lg">
                                    <div className="text-xs text-gray-500 uppercase font-mono">{s.CompanyCode}</div>
                                    <div className="text-2xl font-semibold mt-1">
                                        {s.Success}<span className="text-base text-gray-400">/{s.Total}</span>
                                    </div>
                                    <div className="text-xs space-y-0.5 mt-2">
                                        {s.Failed > 0 && <div className="text-red-600">{s.Failed} failed</div>}
                                        {s.Unparseable > 0 && <div className="text-orange-600">{s.Unparseable} unparseable</div>}
                                        <div className="text-gray-500">last: {new Date(s.LastIngest).toLocaleString('de-CH')}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-3 py-2 text-left font-medium text-gray-700">Co.</th>
                                        <th className="px-3 py-2 text-left font-medium text-gray-700">File</th>
                                        <th className="px-3 py-2 text-center font-medium text-gray-700">Status</th>
                                        <th className="px-3 py-2 text-right font-medium text-gray-700">Rows</th>
                                        <th className="px-3 py-2 text-right font-medium text-gray-700">Duration</th>
                                        <th className="px-3 py-2 text-left font-medium text-gray-700">Model</th>
                                        <th className="px-3 py-2 text-left font-medium text-gray-700">Ingested</th>
                                        <th className="px-3 py-2 text-left font-medium text-gray-700">Error</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {files.length === 0 && (
                                        <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-500">No files ingested yet.</td></tr>
                                    )}
                                    {files.map(f => (
                                        <tr key={f.Id} className="hover:bg-blue-50/30">
                                            <td className="px-3 py-1.5 font-mono text-xs uppercase">{f.CompanyCode}</td>
                                            <td className="px-3 py-1.5">
                                                {f.WebUrl ? (
                                                    <a href={f.WebUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                                                        {f.FileName} <ExternalLink className="w-3 h-3" />
                                                    </a>
                                                ) : f.FileName}
                                            </td>
                                            <td className="px-3 py-1.5 text-center"><StatusPill status={f.Status} /></td>
                                            <td className="px-3 py-1.5 text-right font-mono">{f.RowsExtracted}</td>
                                            <td className="px-3 py-1.5 text-right font-mono text-gray-500">
                                                {f.DurationMs !== null ? `${(f.DurationMs / 1000).toFixed(1)}s` : '—'}
                                            </td>
                                            <td className="px-3 py-1.5 text-xs text-gray-500">{f.ExtractorModel || '—'}</td>
                                            <td className="px-3 py-1.5 text-xs text-gray-500">{new Date(f.IngestedAt).toLocaleString('de-CH')}</td>
                                            <td className="px-3 py-1.5 text-xs text-red-700">{f.ErrorMessage ? f.ErrorMessage.slice(0, 100) : ''}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
